import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { neon, isNeonConfigured } from './neon';
import {
  KILLS_PER_PLAYER,
  DEFAULT_KILL_LINE,
  BET_CAP,
  PRIOR_GAMES,
  FIRST_BLOOD_RATE,
  FIRST_BLOOD_TIER_BONUS,
  FIRST_BLOOD_RATE_TILT,
  FIRST_BLOOD_TILT_CLAMP,
} from '../rules/tuning';
import { getGame, getMode, defaultTierOf, fitTier } from '../rules/games';
import { casualAnswer } from '../rules/casual';

/* 내전 방. 여기부터는 localStorage가 없다.

   방은 계정이 아니라 서버에 있고 여러 명이 같이 본다. 그래서 한 사람의
   기기에 캐시를 두면 그 순간부터 사람마다 다른 화면을 보게 된다.
   항상 서버에서 읽는다.

   포인트/권한을 바꾸는 건 전부 DB 함수(rpc)로만 한다. 브라우저가 DB를
   직접 찌르는 구조라, 테이블 쓰기를 열어주면 콘솔 한 줄로 방장이 될 수 있다.
   테이블 직접 쓰기는 RLS로 막히는 것들(참가자 명단, 경기 기록)만 남겼다. */

const NOT_READY = '서버에 연결되어 있지 않아요.';

const unwrap = ({ data, error }) => {
  if (error) throw new Error(error.message);
  return data;
};

/* RETURNS <테이블타입> 함수는 단일 객체로 오지만, 드라이버에 따라
   한 칸짜리 배열로 오기도 한다. 양쪽 다 받는다 */
const first = (d) => (Array.isArray(d) ? d[0] : d) || null;

/* admin.js도 같은 통로를 쓴다. 오류 처리를 두 벌 두지 않는다 */
export const rpc = async (fn, args) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  try {
    return unwrap(await neon.rpc(fn, args));
  } catch (e) {
    /* PostgREST는 '아직 없는 함수'와 '이름이 틀린 함수'를 같은 말로 알려준다.
       sql/setup.sql을 다시 안 돌린 것뿐인데 원인을 알 수 없는 오류로 보인다 */
    if (/schema cache/i.test(e.message)) {
      throw new Error('DB에 이 기능이 아직 없어요. sql/setup.sql을 다시 실행해 주세요.');
    }
    throw e;
  }
};

/* ---------- 프로필 ---------- */

/* 로그인 직후 딱 한 번. 프로필을 만들고, 달이 넘어갔으면 시즌도 여기서 롤린다 */
/* 못 받아왔으면 null로 조용히 넘기지 않는다. null은 '아직 안 읽음'과
   구분이 안 돼서 화면이 영영 로딩에 머문다 */
export const fetchMe = () =>
  rpc('get_me').then((d) => {
    const me = first(d);
    if (!me) throw new Error('내 정보를 불러오지 못했어요.');
    return me;
  });

export const setNickname = async (nickname) => {
  const name = nickname.trim();
  if (name.length > 12) throw new Error('닉네임은 12자까지예요.');
  if (!isNeonConfigured) throw new Error(NOT_READY);
  /* points/role은 컬럼 GRANT에서 막혀 있다. 여기서 뭘 더 보내도 거절된다 */
  return unwrap(await neon.from('profiles').update({ nickname: name || null }));
};

/* ---------- 방 만들기 / 입장 ---------- */

export const createRoom = (name, game) =>
  rpc('create_room', { p_name: name, p_game: game }).then(first);
export const joinRoom = (code) => rpc('join_room', { p_code: code });

export const getJoinCode = (roomId) => rpc('get_join_code', { p_room: roomId });
export const resetJoinCode = (roomId) => rpc('reset_join_code', { p_room: roomId });

export const setMemberRole = (roomId, userId, role) =>
  rpc('set_member_role', { p_room: roomId, p_user: userId, p_role: role });
export const transferRoom = (roomId, userId) =>
  rpc('transfer_room', { p_room: roomId, p_user: userId });
/* 계정을 바꿔 들어온 사람의 옛 계정을 내보낼 때, 거기 쌓인 끼꼬를
   남겨줄 사람을 지정할 수 있다 (처음 받은 10000은 빼고 넘어간다).
   넘긴 액수를 돌려준다 */
export const kickMember = (roomId, userId, toUserId = null) =>
  rpc('kick_member', { p_room: roomId, p_user: userId, p_to: toUserId });

/* 구글 계정을 바꿨을 때. 내보내기와 달리 그 계정이 남긴 것이 전부
   따라온다 - 배팅 기록·끼꼬 원장·참가자 연결·지난 달 끼꼬.
   안 따라오면 지난 또또 기록이 '알 수 없음'으로 뜬다 */
export const transferAccount = (roomId, fromUserId, toUserId) =>
  rpc('transfer_account', { p_room: roomId, p_from: fromUserId, p_to: toUserId });

/* 이 역할에게 이 기능을 줄지 뺄지. 이름은 rules/permissions.js의 ROLES·CAPS와,
   막는 쪽은 setup.sql의 room_can과 같아야 한다 */
export const setRoleCap = (roomId, role, cap, on) =>
  rpc('set_role_cap', { p_room: roomId, p_role: role, p_cap: cap, p_on: on });

/* 멤버 ↔ 참가자 묶기. playerId가 null이면 연결을 끊는다.
   묶어두면 경기 참여 포인트가 이 계정으로 간다 */
export const linkRoomPlayer = (roomId, userId, playerId) =>
  rpc('link_room_player', { p_room: roomId, p_user: userId, p_player: playerId ?? null });

/* 사이트를 안 쓰는 친구 몫의 자리표시자 계정. 로그인은 못 하고
   참가자와 묶이기 위해서만 존재한다 */
export const addGhostMember = (roomId, name) =>
  rpc('add_ghost_member', { p_room: roomId, p_name: name });
export const removeGhostMember = (roomId, userId) =>
  rpc('remove_ghost_member', { p_room: roomId, p_user: userId });

/* 방장이 끼꼬를 직접 더하거나 뺀다. 성공하면 서버가 반드시 로그를 남긴다 */
export const adjustPoints = (roomId, userId, delta, reason) =>
  rpc('adjust_points', {
    p_room: roomId,
    p_user: userId,
    p_delta: delta,
    p_reason: reason || null,
  });
export const leaveRoom = (roomId) => rpc('leave_room', { p_room: roomId });
export const deleteRoom = (roomId) => rpc('delete_room', { p_room: roomId });

/* 방 색과 엠블럼. 값 검사는 DB의 CHECK가 한다(rooms_accent_chk).
   화면에서 막아도 콘솔로는 얼마든지 보낼 수 있어서 마지막 문은 저쪽이다 */
export const setRoomStyle = async (roomId, patch) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  return unwrap(await neon.from('rooms').update(patch).eq('id', roomId));
};

/* 지난 달들의 박제. 방 대문과 명예의 전당이 같은 값을 봐야 해서
   방 화면에서 한 번만 읽고 나눠 쓴다 */
export const useHallOfFame = (roomId) => {
  const fetcher = useCallback(() => fetchHallOfFame(roomId), [roomId]);
  const { data } = useFetch(fetcher, Boolean(roomId) && isNeonConfigured);
  /* data가 null인 동안 매번 새 []를 만들면 아래 useMemo가 렌더마다 다시 돈다 */
  const rows = useMemo(() => data || [], [data]);

  /* 제일 최근에 끝난 달의 1등. 대문에 걸어두는 그 사람이다 */
  const champion = useMemo(() => {
    if (rows.length === 0) return null;
    const latest = rows.reduce((a, b) => (b.month > a.month ? b : a)).month;
    return rows
      .filter((r) => r.month === latest)
      .sort((a, b) => b.kkiko_points - a.kkiko_points)[0];
  }, [rows]);

  return { hofRows: rows, champion };
};

export const fetchHallOfFame = async (roomId) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  return unwrap(
    await neon
      .from('hall_of_fame')
      .select('month,user_id,display_name,kkiko_points')
      .eq('room_id', roomId)
      .order('month', { ascending: false })
  );
};

export const renameRoom = async (roomId, name) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  return unwrap(await neon.from('rooms').update({ name: name.trim() }).eq('id', roomId));
};

export const transferPoints = (roomId, toUserId, amount) =>
  rpc('transfer_points', { p_room: roomId, p_to: toUserId, p_amount: amount });

/* ---------- 포인트 내역 · 피드 ---------- */

/* RLS가 내 행만 준다. 방을 가려낼 필요도 없다 */
/* 로그 탭과 같은 방식. 한 화면에 한 쪽씩만 보여주고 커서로 넘긴다.
   끝없이 이어 붙이면 어디까지 봤는지 놓친다 */
export const LEDGER_PAGE = 15;

export const fetchLedger = async (beforeId) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  let q = neon
    .from('point_ledger')
    .select('id,room_id,delta,reason,counterpart_user_id,created_at')
    .order('id', { ascending: false })
    .limit(LEDGER_PAGE);
  if (beforeId) q = q.lt('id', beforeId);
  return unwrap(await q);
};

export const FEED_PAGE = 20;

/* 커서 방식. 전체를 다시 받지 않고 마지막 id보다 작은 것만 이어 받는다.

   걸러내기는 서버에서 한다. 받아온 20줄에서 화면이 골라내면 페이지마다
   보이는 줄 수가 달라지고, 운 나쁘면 통째로 빈 페이지가 나온다.
   방마다 로그는 500줄로 잘려 있고 (room_id, id desc) 인덱스를 그대로
   타므로, type 조건이 붙어도 부담이 늘지 않는다 */
export const fetchLogs = async (roomId, beforeId, types = null) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  let q = neon
    .from('room_logs')
    .select('id,type,payload,created_at')
    .eq('room_id', roomId)
    .order('id', { ascending: false })
    .limit(FEED_PAGE);
  if (beforeId) q = q.lt('id', beforeId);
  if (types && types.length > 0) q = q.filter('type', 'in', `(${types.join(',')})`);
  return unwrap(await q);
};

const num = (n) => Number(n || 0).toLocaleString();

/* 로그 한 줄을 조각으로 나눠 돌려준다.
   통 문자열로 주면 화면에서 이름과 금액에 색을 못 입힌다. 무엇이 이름이고
   무엇이 금액인지는 여기서만 알고 있으므로, 조각으로 잘라 넘긴다.

   tag는 이 줄이 무엇에 관한 것인지 (경기/이체/배팅). 목록에서 눈으로
   훑을 때 종류부터 걸러진다. */
const t = (v) => ({ k: 'text', v });
/* 초록(hot)은 '좋은 일이 일어났다', 빨강(bad)은 '되돌렸다·깎였다·취소했다'.
   전부 초록으로 두면 취소 로그가 축하처럼 보인다 */
const bad = (v) => ({ k: 'bad', v });
const nameOf_ = (v) => ({ k: 'name', v });
const amountOf = (v) => ({ k: 'amount', v: `${num(v)} 끼꼬` });

/* 태그는 이 방에서 실제로 쓰는 말로. '배팅'이라고 적어두면
   화면 어디에도 없는 단어라 한 박자 늦게 읽힌다 */
export const LOG_TAGS = {
  transfer: { label: '끼꼬', tone: 'blue' },
  betting_open: { label: '또또', tone: 'purple' },
  /* 일반 게임 또또도 '또또' 거르기에 같이 묶인다 */
  casual_open: { label: '또또', tone: 'purple' },
  casual_settled: { label: '또또', tone: 'purple' },
  betting_locked: { label: '또또', tone: 'purple' },
  settled: { label: '경기', tone: 'gold' },
  settle_undone: { label: '정정', tone: 'red' },
  adjust: { label: '조정', tone: 'red' },
  record: { label: '신기록', tone: 'gold' },
  scrim_cancelled: { label: '취소', tone: 'red' },
  player_merge: { label: '명단', tone: 'blue' },
  member_kicked: { label: '멤버', tone: 'red' },
  account_moved: { label: '계정', tone: 'blue' },
};

/* 로그 탭의 걸러내기 단추. 줄에 붙는 라벨이 곧 단추 이름이라, LOG_TAGS에서
   그대로 만들어낸다 - 종류를 하나 더할 때 여기를 같이 고칠 일이 없다.
   (또또처럼 라벨 하나에 종류가 여러 개 묶인 것도 한 단추로 모인다) */
export const FEED_FILTERS = [
  { label: '전체', types: null },
  ...Object.entries(LOG_TAGS).reduce((out, [type, tag]) => {
    const found = out.find((f) => f.label === tag.label);
    if (found) found.types.push(type);
    else out.push({ label: tag.label, tone: tag.tone, types: [type] });
    return out;
  }, []),
];

/* 방 기록 종류. 서버가 kind만 보내고 문구는 여기서 만든다 */
const RECORD_LABEL = {
  kills: '한 판 최다 킬',
  bet: '한 판 최다 또또 판돈',
};

/* '2분 뒤 자동 마감'처럼 사람이 읽는 길이로. 초 단위는 필요 없다 */
const durationText = (fromIso, toIso) => {
  const sec = Math.round((new Date(toIso) - new Date(fromIso)) / 1000);
  if (!Number.isFinite(sec) || sec <= 0) return null;
  if (sec < 60) return `${sec}초`;
  const min = Math.round(sec / 60);
  return `${min}분`;
};

export const feedParts = (log) => {
  const p = log.payload || {};
  const tag = LOG_TAGS[log.type] || { label: '기타', tone: 'gray' };

  switch (log.type) {
    /* 누가 무엇을 했는지가 먼저 오게 쓴다. '철수 → 영희 1,000 끼꼬 보냄'처럼
       조각만 늘어놓으면 훑을 때마다 머리로 문장을 다시 만들어야 한다 */
    case 'transfer':
      return {
        tag,
        parts: [
          nameOf_(p.from),
          t(' 님이 '),
          nameOf_(p.to),
          t(' 님에게 '),
          amountOf(p.amount),
          t('를 보냈어요'),
        ],
      };
    case 'betting_open': {
      const dur = p.closes_at ? durationText(log.created_at, p.closes_at) : null;
      return {
        tag,
        parts: [
          nameOf_(`${p.size}인 내전`),
          t('에 '),
          { k: 'hot', v: '또또가 열렸어요' },
          ...(dur ? [t(` · ${dur} 뒤 자동 마감`)] : []),
        ],
      };
    }
    case 'casual_open': {
      const dur = p.closes_at ? durationText(log.created_at, p.closes_at) : null;
      return {
        tag,
        parts: [
          nameOf_(p.mode === 'aram' ? '칼바람' : '일반 게임'),
          t('에 '),
          { k: 'hot', v: '또또가 열렸어요' },
          ...(dur ? [t(` · ${dur} 뒤 자동 마감`)] : []),
        ],
      };
    }
    case 'casual_settled':
      return {
        tag,
        parts: [
          { k: 'hot', v: '일반 게임 정산' },
          ...(p.kills == null ? [] : [t(' · 총 킬 '), nameOf_(String(p.kills))]),
          ...(p.bet_total
            ? [t(' · 또또 '), amountOf(p.bet_total), t('가 오갔어요')]
            : [t(' · 정산 끝')]),
        ],
      };
    case 'betting_locked':
      return {
        tag,
        parts: [
          t('또또 마감 · '),
          nameOf_(`${p.people}명`),
          t('이 '),
          amountOf(p.total),
          t('를 걸었어요'),
        ],
      };
    case 'settled':
      return {
        tag,
        parts: [
          { k: 'hot', v: p.winner === 'A' ? '1팀 승리' : '2팀 승리' },
          ...(p.kills == null ? [] : [t(' · 총 킬 '), nameOf_(String(p.kills))]),
          ...(p.bet_total
            ? [t(' · 또또 '), amountOf(p.bet_total), t('를 나눠 가졌어요')]
            : [t(' · 정산 끝')]),
        ],
      };
    /* 방장이 잔액을 손댄 줄. 누구를 얼마나 왜 만졌는지 다 적는다.
       조용히 지나갈 수 있으면 방장을 믿을 근거가 없어진다 */
    case 'adjust':
      return {
        tag,
        parts: [
          t('방장이 '),
          nameOf_(p.who),
          t(' 님 끼꼬를 '),
          p.delta > 0
            ? { k: 'hot', v: `${num(Math.abs(p.delta))}` }
            : bad(`${num(Math.abs(p.delta))}`),
          t(p.delta > 0 ? ' 올렸어요' : ' 내렸어요'),
          t(` · 남은 ${num(p.after)}`),
          ...(p.reason ? [t(' · '), nameOf_(p.reason)] : []),
        ],
      };
    /* 방 기록이 깨졌다. 지나가면 기록이 있으나 마나라 금색으로 세워둔다 */
    case 'record':
      return {
        tag,
        parts: [
          { k: 'hot', v: '🏆 신기록' },
          t(` · ${RECORD_LABEL[p.kind] || p.kind} `),
          nameOf_(String(num(p.value))),
          ...(p.prev ? [t(` (지난 기록 ${num(p.prev)})`)] : [t(' (첫 기록)')]),
        ],
      };
    /* 진행 중이던 판을 통째로 없앴다. 남의 돈이 오간 일이라 반드시 남긴다 */
    case 'scrim_cancelled':
      return {
        tag,
        parts: [
          t(p.by === 'admin' ? '관리자가 또또를 ' : '방장이 또또를 '),
          bad('취소'),
          t('했어요 · '),
          nameOf_(`${p.people}명`),
          t(' '),
          amountOf(p.refund),
          t(' 환불'),
        ],
      };
    /* 계정을 바꿔 들어온 사람의 옛 계정을 내보낼 때, 거기 쌓인 끼꼬가
       남에게 넘어간다. 남의 돈이 움직이는 일이라 반드시 남긴다 */
    case 'member_kicked':
      return {
        tag,
        parts: [
          t('방장이 '),
          nameOf_(p.who),
          t(' 님을 '),
          bad('내보냈어요'),
          ...(p.amount > 0
            ? [t(' · 끼꼬 '), amountOf(p.amount), t('를 '), nameOf_(p.to), t(' 님에게 넘김')]
            : []),
        ],
      };
    /* 전적이 두 줄로 갈려 있던 걸 합쳤다. 남의 전적이 움직이는 일이라 남긴다 */
    case 'player_merge':
      return {
        tag,
        parts: [
          nameOf_(p.drop),
          t(' 을(를) '),
          nameOf_(p.keep),
          t('에 합쳤어요'),
          ...(p.games ? [t(` · 경기 ${p.games}판`)] : []),
        ],
      };
    /* 같은 사람이 계정을 바꿨다. 그 계정이 남긴 기록이 통째로 옮겨간다 */
    case 'account_moved':
      return {
        tag,
        parts: [
          nameOf_(p.from),
          t(' 님의 기록을 '),
          nameOf_(p.to),
          t(' 님 계정으로 옮겼어요'),
          ...(p.amount > 0 ? [t(' · 끼꼬 '), amountOf(p.amount)] : []),
        ],
      };
    case 'settle_undone':
      return {
        tag,
        parts: [t('방장이 정산을 '), bad('되돌렸어요'), t(` (${p.count}번째)`)],
      };
    default:
      return { tag, parts: [t(log.type)] };
  }
};

/* 한 줄 문자열이 필요한 곳(알림 등)을 위해 남겨둔다 */
export const feedLine = (log) =>
  feedParts(log)
    .parts.map((x) => x.v)
    .join('');

/* ---------- 참가자 명단 ---------- */
/* RLS가 방장·부방장만 쓰게 막는다. 돈이 안 걸린 데이터라 함수까지는 필요 없다 */

/* 같은 이름이 예전에 있었다면 그 행을 되살린다.
   새로 넣어버리면 id가 달라져서 지난 경기의 그 사람과 남남이 된다 */
export const addRoomPlayer = async (roomId, player, game) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  const name = String(player.name || '').trim();
  /* 기본 티어는 게임마다 다르다. 발로란트에는 디비전 4가 없다.
     넘어온 티어가 그 게임에 없는 값이면(롤 에메랄드 → 발로란트)
     기본값으로 갈아끼운다. 안 그러면 화면에 없는 티어가 저장된다 */
  const base = defaultTierOf(game);
  const fitted = player.tier ? fitTier(game, player) : null;

  const gone = unwrap(
    await neon
      .from('room_players')
      .select('id')
      .eq('room_id', roomId)
      .eq('name', name)
      .not('deleted_at', 'is', null)
      .limit(1)
  );
  if (gone && gone.length > 0) {
    return unwrap(
      await neon
        .from('room_players')
        .update({ deleted_at: null, ...base, ...player, ...fitted, name })
        .eq('id', gone[0].id)
    );
  }

  return unwrap(
    await neon
      .from('room_players')
      .insert({ room_id: roomId, ...base, ...player, ...fitted, name })
  );
};

/* 이름만 바꾸면 끝이다. 경기는 이 행의 id를 보고 있어서 손댈 게 없다 */
export const updateRoomPlayer = async (id, patch) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  return unwrap(await neon.from('room_players').update(patch).eq('id', id));
};

/* 진짜로 지우지 않는다. 경기가 이 행의 id를 들고 있어서, 지우면
   지난 기록에서 그 사람이 '?'가 되고 전적도 남남이 된다.
   명단에서만 감추고, 같은 이름을 다시 넣으면 이 행이 되살아난다 */
export const removeRoomPlayer = async (id) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);
  return unwrap(
    await neon
      .from('room_players')
      .update({ deleted_at: new Date().toISOString(), linked_user_id: null })
      .eq('id', id)
  );
};

/* ---------- 경기 ---------- */
/* 경기에 참여 포인트가 붙으면서 직접 insert를 열어둘 수 없게 됐다.
   열어두면 경기를 찍어내는 것만으로 끼꼬를 무한히 만들 수 있다 */

export const addScrim = ({ roomId, mode, teamA, teamB, winner }) =>
  rpc('record_scrim', {
    p_room: roomId,
    p_mode: mode,
    p_team_a: teamA,
    p_team_b: teamB,
    p_winner: winner,
  });

export const removeScrim = (id) => rpc('delete_scrim', { p_scrim: id });

/* 기록지는 이름을 손으로 친다. 명단에 없는 이름이 나오면 참가자로 먼저
   등록하고 그 id로 경기를 남긴다. 그래야 손님으로 한 판 뛴 사람도
   다음부터 이름을 골라 쓸 수 있고, 전적이 한 사람으로 모인다 */
const toIds = async (roomId, teamA, teamB, players, game) => {
  if (!isNeonConfigured) throw new Error(NOT_READY);

  const idOf = new Map(players.map((p) => [p.name, p.id]));
  const missing = [...new Set([...teamA, ...teamB])].filter((n) => !idOf.has(n));
  if (missing.length === 0) {
    return [teamA.map((n) => idOf.get(n)), teamB.map((n) => idOf.get(n))];
  }

  /* 명단에서 지웠던 사람이면 그 행을 되살린다. 새로 넣으면 id가 달라져서
     지난 경기의 그 사람과 전적이 갈린다 */
  const gone =
    unwrap(
      await neon
        .from('room_players')
        .select('id,name')
        .eq('room_id', roomId)
        .in('name', missing)
        .not('deleted_at', 'is', null)
    ) || [];

  if (gone.length > 0) {
    await neon
      .from('room_players')
      .update({ deleted_at: null })
      .in(
        'id',
        gone.map((r) => r.id)
      );
    gone.forEach((r) => idOf.set(r.name, r.id));
  }

  const fresh = missing.filter((n) => !idOf.has(n));
  if (fresh.length > 0) {
    const rows = unwrap(
      await neon
        .from('room_players')
        .insert(fresh.map((name) => ({ room_id: roomId, name, ...defaultTierOf(game) })))
        .select()
    );
    (rows || []).forEach((r) => idOf.set(r.name, r.id));
  }

  return [teamA.map((n) => idOf.get(n)), teamB.map((n) => idOf.get(n))];
};

export const addScrimByNames = async ({ roomId, mode, teamA, teamB, winner, players, game }) => {
  const [a, b] = await toIds(roomId, teamA, teamB, players, game);
  return addScrim({ roomId, mode, teamA: a, teamB: b, winner });
};

export const openBettingByNames = async ({
  roomId,
  mode,
  teamA,
  teamB,
  players,
  closeSeconds = null,
  killLine = null,
  game,
}) => {
  const [a, b] = await toIds(roomId, teamA, teamB, players, game);
  return openBetting(roomId, mode, a, b, closeSeconds, killLine);
};

/* scrims는 이름 대신 room_players.id를 담는다. 이름은 여기서 붙인다.
   참가자가 50명뿐이라 맵 한 번이면 되고, 그 대가로 matches.js의 집계를
   한 줄도 안 고치고 그대로 쓴다.

   명단에서 지워진 참가자의 id는 남아 있을 수 있다. 그 자리는 버린다 */
export const toMatches = (scrims = [], players = [], game) => {
  const nameOf = new Map(players.map((p) => [p.id, p.name]));
  const names = (ids) => (ids || []).map((id) => nameOf.get(id)).filter(Boolean);
  return scrims
    /* 일반 큐 또또는 전적이 아니다. 승자를 안 남기므로 어차피 빠지지만,
       나중에 누가 casual에 winner를 넣더라도 새지 않게 못 박아둔다 */
    .filter((s) => s.kind !== 'casual' && (s.winner === 'A' || s.winner === 'B'))
    .map((s) => ({
      id: s.id,
      mode: s.mode,
      teamA: names(s.team_a),
      teamB: names(s.team_b),
      winner: s.winner,
      playedAt: new Date(s.played_at).getTime(),
      /* 지울 때 '또또까지 되돌아간다'고 알려주려면 필요하다 */
      betCount: s.bet_count || 0,
      betTotal: s.bet_total || 0,
      /* 결과를 넣은 판에만 있다. 또또 없이 남긴 기록은 비어 있다 */
      totalKills: s.total_kills ?? null,
      firstBlood: nameOf.get(s.first_blood_player_id) || null,
      /* 그 판에 실제로 쓴 기준선. 저장된 값만 쓴다.
         없다고 지금 인원으로 다시 계산하면, 계산에 쓰는 숫자를 고치는
         순간 지난 기록의 오버/언더가 통째로 뒤집힌다. 모르면 안 적는 게
         틀리게 적는 것보다 낫다 */
      killLine: s.kill_line == null ? null : Number(s.kill_line),
    }))
    .sort((a, b) => a.playedAt - b.playedAt);
};

/* ---------- 읽기 ---------- */

/* 로딩/에러/재조회를 매번 손으로 쓰지 않으려고 한 겹만 둔다.
   fetcher는 호출하는 쪽에서 useCallback으로 묶어서 넘길 것 */
/* 한 번 어긋났다고 포기하지 않는다.

   두 가지 자리에서 실제로 실패한다.
   - 정산처럼 요청이 한꺼번에 몰릴 때
   - Neon 무료 플랜은 놀고 있으면 컴퓨트를 재우는데, 깨어나는 첫 요청이
     몇 초 걸리거나 그대로 끊긴다 ('가끔 들어가면 방이 없다'가 이것)

   1초, 2초, 3초 뒤로 미루며 세 번까지. 6초쯤이면 깨어난다 */
const RETRY_MS = 1000;
const MAX_RETRIES = 3;

const useFetch = (fetcher, enabled = true) => {
  const [state, setState] = useState({ loading: enabled, data: null, error: null });
  /* 응답이 늦게 도착한 옛 요청이 새 화면을 덮어쓰지 않게 한다 */
  const seq = useRef(0);
  const fails = useRef(0);
  const timer = useRef(null);

  const reload = useCallback(async () => {
    clearTimeout(timer.current);
    if (!enabled) {
      setState({ loading: false, data: null, error: null });
      return;
    }
    const mine = (seq.current += 1);
    /* 이미 보여줄 게 있으면 loading을 켜지 않는다.
       켜면 화면이 통째로 스켈레톤으로 바뀌면서 그 아래 컴포넌트가 언마운트되고,
       담고 있던 배팅이나 입력 중이던 값이 날아간다. 남이 배팅할 때마다
       (version이 올라 폴링이 돈다) 내 화면이 초기화되던 게 이것 때문이다 */
    setState((s) => ({ ...s, loading: s.data === null }));
    try {
      const data = await fetcher();
      if (seq.current !== mine) return;
      fails.current = 0;
      setState({ loading: false, data, error: null });
    } catch (e) {
      if (seq.current !== mine) return;
      fails.current += 1;
      const again = fails.current <= MAX_RETRIES;
      if (again) timer.current = setTimeout(reload, RETRY_MS * fails.current);
      setState((s) => {
        /* 들고 있던 게 있으면 그대로 둔다. 정산하자마자 방이 사라져
           목록으로 나갔다 다시 들어와야 하던 게 이것 때문이었다 */
        if (s.data !== null) return { ...s, loading: false };
        /* 첫 판부터 실패한 경우. 다시 시도하는 동안은 계속 '읽는 중'이다.
           여기서 loading을 내려버리면 '방이 없음'으로 보인다 */
        return { loading: again, data: null, error: again ? null : e.message };
      });
    }
  }, [fetcher, enabled]);

  useEffect(() => {
    reload();
    return () => {
      seq.current += 1;
      clearTimeout(timer.current);
    };
  }, [reload]);

  /* enabled가 false → true로 바뀌는 순간(로그인 확인이 끝나는 때)에는
     아직 state가 옛 값이라 loading: false, data: null이다. 그 한 렌더 동안
     화면이 '아직 아무것도 없음'으로 보인다. 받아온 게 없고 오류도 없으면
     아직 읽는 중인 것이다 */
  const loading = state.loading || (enabled && state.data === null && state.error === null);

  return { ...state, loading, reload };
};

export const useMe = (userId) => {
  const fetcher = useCallback(() => fetchMe(), []);
  const { data, loading, error, reload } = useFetch(
    fetcher,
    Boolean(userId) && isNeonConfigured
  );
  return { me: data, loading, error, reload };
};

/* ready: 서버가 나를 알아본 게 확인된 뒤에만 읽는다.

   RLS는 토큰이 없으면 오류를 내지 않고 '0행'을 돌려준다. 그래서 로그인
   직후 토큰이 아직 안 붙은 채로 요청이 나가면, 멀쩡히 성공하면서 빈 목록이
   오고 화면에는 '아직 들어간 방이 없어요'가 뜬다. 새로고침하면 낫는 게
   이것 때문이다 - 오류가 아니라서 재시도도 안 걸린다.

   get_me()는 auth.user_id()가 없으면 반드시 실패하므로, 그게 한 번
   성공했다는 건 토큰이 확실히 붙었다는 뜻이다. 그걸 기다렸다 읽는다. */
export const useMyRooms = (userId, ready = true) => {
  const fetcher = useCallback(async () => {
    /* RLS가 내가 멤버인 방만 돌려준다. 따로 걸 조건이 없다 */
    const rooms = unwrap(
      await neon.from('rooms').select('id,name,owner_id,version,created_at,accent,emblem,game')
    );
    const members = unwrap(await neon.from('room_members').select('room_id,user_id,role'));
    /* 방마다 내 끼꼬가 다르므로 목록에서도 방별로 보여준다 */
    const wallets = unwrap(await neon.from('room_wallets').select('room_id,user_id,points'));
    /* '지금 또또 중인 방'은 목록에서 제일 먼저 보고 싶은 것이다.
       끝난 경기는 안 읽는다 - 진행 중인 것만 몇 줄 가져온다 */
    const live = unwrap(
      await neon.from('scrims').select('room_id,status').in('status', ['betting', 'locked'])
    );
    /* '최근 플레이순'으로 세우려면 방마다 마지막 판이 언제였는지가 필요하다.
       방마다 한 번씩 묻지 않고, 최근 것부터 한 묶음만 받아 훑는다.
       먼저 나오는 게 그 방의 마지막 판이다 (내림차순이므로).
       그보다 오래 안 한 방은 아예 안 걸리는데, 어차피 맨 뒤로 간다 */
    const recent = unwrap(
      await neon
        .from('scrims')
        .select('room_id,played_at')
        .order('played_at', { ascending: false })
        .limit(200)
    );
    const lastPlayed = new Map();
    (recent || []).forEach((sc) => {
      if (!lastPlayed.has(sc.room_id)) lastPlayed.set(sc.room_id, new Date(sc.played_at).getTime());
    });
    const liveOf = new Map((live || []).map((sc) => [sc.room_id, sc.status]));
    const myPoints = new Map(
      (wallets || []).filter((w) => w.user_id === userId).map((w) => [w.room_id, w.points])
    );
    const countOf = new Map();
    members.forEach((m) => countOf.set(m.room_id, (countOf.get(m.room_id) || 0) + 1));
    const roleOf = new Map(
      members.filter((m) => m.user_id === userId).map((m) => [m.room_id, m.role])
    );
    return (rooms || [])
      .map((r) => ({
        ...r,
        memberCount: countOf.get(r.id) || 0,
        myRole: roleOf.get(r.id),
        myPoints: myPoints.get(r.id) ?? 0,
        live: liveOf.get(r.id) || null,
        lastPlayed: lastPlayed.get(r.id) ?? 0,
      }))
      /* 순서는 화면이 정한다(lib/roomPrefs). 핀과 고른 정렬이 여기
         없으니 여기서 세워봐야 한 번 더 세워야 한다 */
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [userId]);

  const { data, loading, error, reload } = useFetch(
    fetcher,
    Boolean(userId) && ready && isNeonConfigured
  );
  /* 아직 기다리는 중인데 '없음'으로 보이면 안 된다 */
  return { rooms: data || [], loading: loading || (Boolean(userId) && !ready), error, reload };
};

/* 방 상세를 몇 번에 나눠 받을지가 곧 DB 부하다.
   PostgREST가 FK를 따라 한 번에 묶어주므로 방+멤버+참가자+경기는 한 요청이다.
   프로필만 FK가 없어 따로 받는다 (RLS가 같은 방 사람으로 이미 좁혀준다) */
const ROOM_SELECT =
  'id,name,owner_id,version,created_at,accent,emblem,game,role_caps,' +
  'room_members(user_id,role,joined_at,is_ghost),' +
  'room_players(id,name,tier,division,linked_user_id,deleted_at),' +
  'scrims(id,mode,team_a,team_b,winner,played_at,status,total_kills,' +
  /* 여기서 컬럼을 빼먹으면 화면이 조용히 틀린 값을 보여준다. 오류도 안 난다.
     - betting_closes_at이 없으면 마감 타이머가 아예 안 그려지고
     - kill_line이 없으면 방장이 직접 정한 기준선이 무시되고
       인원으로 계산한 값이 뜬다 (저장은 되는데 안 읽는 것) */
  'first_blood_player_id,bet_total,bet_count,undo_count,locked_at,' +
  /* 일반 큐 또또. kind를 안 받으면 전적에서 걸러낼 수가 없고,
     fb_side·first_dragon·lanes가 없으면 결과와 배당을 못 그린다 */
  'kind,fb_side,first_dragon,lanes,our_kills,opp_kills,fb_enemy_lane,fb_ally_lane,our_win,' +
  'betting_closes_at,kill_line)';

/* 탭이 보일 때만. 실시간 구독이 없어 폴링이 불가피한데 방 전체를 매번
   읽으면 그게 곧 부하다. version 한 컬럼(기본키 조회)만 보고, 값이
   달라졌을 때만 상세를 다시 받는다.

   평소 30초는 '누가 경기를 기록했나' 정도에는 충분하지만, 또또가 열려
   있는 동안에는 다들 화면을 보고 있다. 누가 걸었는지, 마감됐는지,
   결과가 나왔는지를 30초 뒤에 알면 화면이 멈춘 것처럼 느껴진다.
   그때만 5초로 당긴다 - 열 명이 보고 있어도 초당 두 번이고, 또또가
   끝나면 바로 30초로 돌아간다 */
const POLL_MS = 30000;
const HOT_MS = 5000;

export const useRoom = (roomId, userId) => {
  const fetcher = useCallback(async () => {
    const room = unwrap(
      await neon.from('rooms').select(ROOM_SELECT).eq('id', roomId).maybeSingle()
    );
    if (!room) throw new Error('방을 찾을 수 없어요. 나갔거나 삭제된 방입니다.');
    /* 잔액은 이제 방마다 따로다. 프로필에는 이름과 동의 여부만 남는다 */
    const profiles = unwrap(
      await neon.from('profiles').select('user_id,nickname,agreed_fairplay_at')
    );
    const wallets = unwrap(
      await neon.from('room_wallets').select('user_id,points').eq('room_id', roomId)
    );
    return { room, profiles: profiles || [], wallets: wallets || [] };
  }, [roomId]);

  const enabled = Boolean(roomId) && Boolean(userId) && isNeonConfigured;
  const { data, loading, error, reload } = useFetch(fetcher, enabled);

  const version = data?.room?.version;
  /* 또또가 열려 있거나 마감돼 결과를 기다리는 중인가. 그때만 자주 본다 */
  const hot = Boolean(
    data?.room?.scrims?.some((s) => s.status === 'betting' || s.status === 'locked')
  );

  useEffect(() => {
    if (!enabled || version === undefined) return undefined;
    let timer;
    /* 치운 뒤에 날아온 응답으로 reload를 부르지 않게 */
    let stopped = false;

    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const row = unwrap(
          await neon.from('rooms').select('version').eq('id', roomId).maybeSingle()
        );
        if (!stopped && row && row.version !== version) reload();
      } catch {
        /* 잠깐 끊긴 것뿐이다. 다음 차례에 다시 본다 */
      }
    };

    /* 무슨 일이 있어도 다음 차례를 다시 잡는다. check 뒤에 그냥 return하면,
       실패하거나 값이 그대로일 때 타이머가 끊겨 폴링이 조용히 죽는다 */
    const tick = async () => {
      await check();
      if (!stopped) timer = setTimeout(tick, hot ? HOT_MS : POLL_MS);
    };
    timer = setTimeout(tick, hot ? HOT_MS : POLL_MS);

    /* 탭으로 돌아온 순간이 제일 궁금한 때다. 다음 차례까지 기다리지 않는다.
       (숨길 때도 불리지만 check가 바로 돌아 나간다) */
    document.addEventListener('visibilitychange', check);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
    };
  }, [enabled, roomId, version, reload, hot]);

  /* 여기부터는 받아온 것을 화면이 쓰는 모양으로 푸는 일이다. 방이 다시
     그려질 때마다(탭을 옮기거나 팝업을 열 때마다) 전부 다시 돌면, 경기
     전체를 훑는 toMatches와 '없는 id 찾기'가 매번 같이 돈다.
     받아온 것이 바뀔 때만 푼다 - 결과가 같은 객체로 유지되니 아래쪽
     화면들의 useMemo도 같이 살아난다 */
  const derived = useMemo(() => {
    const room = data?.room || null;
    const members = room?.room_members || [];
    /* 지운 사람도 들고 있는다. 지난 경기의 이름을 붙이려면 필요하다 */
    const allPlayers = room?.room_players || [];
    const players = allPlayers.filter((p) => !p.deleted_at);
    const profileOf = new Map((data?.profiles || []).map((p) => [p.user_id, p]));
    const walletOf = new Map((data?.wallets || []).map((w) => [w.user_id, w.points]));

    const scrims = room?.scrims || [];

    /* 경기가 가리키는데 명단에 아예 없는 id.

       참가자를 지워도 행은 남기지만(deleted_at), 그 장치가 생기기 전에는
       진짜로 지웠다. 그 시절 기록이 남아 있으면 그 자리가 화면에서 '?'가
       되거나 아예 빠져서 5명이 4명으로 보인다.

       '#23' 하나만 보여주면 그게 누구였는지 알 길이 없다. 언제 몇 판
       뛰었는지, 누구와 같은 팀이었는지를 같이 모아서 넘긴다 - 사람은
       그걸 보고 기억해낸다. */
    const knownIds = new Set(allPlayers.map((p) => Number(p.id)));
    const nameOfId = new Map(allPlayers.map((p) => [Number(p.id), p.name]));
    const lost = new Map();

    scrims.forEach((s) => {
      const at = new Date(s.played_at).getTime();
      [s.team_a || [], s.team_b || []].forEach((team) => {
        const ids = team.map(Number).filter(Number.isFinite);
        ids.forEach((id) => {
          if (knownIds.has(id)) return;
          const cur = lost.get(id) || {
            id,
            games: 0,
            first: at,
            last: at,
            mates: new Map(),
          };
          cur.games += 1;
          cur.first = Math.min(cur.first, at);
          cur.last = Math.max(cur.last, at);
          /* 같은 팀이었던 사람. 이름이 있는 쪽만 센다 */
          ids.forEach((other) => {
            const nm = other === id ? null : nameOfId.get(other);
            if (nm) cur.mates.set(nm, (cur.mates.get(nm) || 0) + 1);
          });
          lost.set(id, cur);
        });
      });
    });

    const lostPlayers = [...lost.values()]
      .map((x) => ({
        ...x,
        mates: [...x.mates.entries()]
          .map(([name, n]) => ({ name, n }))
          .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name, 'ko')),
      }))
      .sort((a, b) => b.games - a.games || a.id - b.id);

    return {
      room,
      scrims,
      /* 아직 안 끝난 배팅 경기는 방에 하나뿐이다 (open_betting이 막는다) */
      activeScrim: scrims.find((s) => s.status === 'betting' || s.status === 'locked') || null,
      players: [...players].sort((a, b) => a.name.localeCompare(b.name, 'ko')),
      /* 지운 사람까지. 지난 경기의 이름을 붙이려면 이쪽을 봐야 한다 */
      allPlayers,
      lostPlayers,
      matches: toMatches(room?.scrims, allPlayers, room?.game),
      members: members
        .map((m) => ({
          ...m,
          nickname: profileOf.get(m.user_id)?.nickname || '이름 없음',
          points: walletOf.get(m.user_id) ?? 0,
          agreed: Boolean(profileOf.get(m.user_id)?.agreed_fairplay_at),
          /* 이 계정이 명단의 누구인지. 연결이 없으면 null */
          player: players.find((p) => p.linked_user_id === m.user_id) || null,
        }))
        .sort((a, b) => ROLE_ORDER[a.role] - ROLE_ORDER[b.role]),
      myRole: members.find((m) => m.user_id === userId)?.role || null,
      /* 이름을 정했는지. members의 nickname은 '이름 없음'으로 채워져 있어
         안 정한 것과 구분이 안 된다. 원본 프로필을 그대로 본다 */
      myNickname: profileOf.get(userId)?.nickname || null,
    };
  }, [data, userId]);

  return { loading, error, reload, ...derived };
};

const ROLE_ORDER = { owner: 0, admin: 1, staff: 2, member: 3 };

export const ROLE_LABEL = { owner: '방장', admin: '부방장', staff: '운영진', member: '멤버' };

/* ---------- 또또 (배팅) ---------- */

/* 킬 기준선.

   인원에 따라 달라진다. 8명이면 6명보다 킬이 그만큼 더 나오니
   같은 값을 쓰면 한쪽이 거의 확정이 된다.

   처음엔 인당 7.5킬(6명 45.5)로 잡았는데 매번 오버만 떴다. 실제로는
   그보다 훨씬 많이 나온다는 뜻이라 인당 8.9킬로 올린다.
   6명 53.5 · 8명 71.5 · 10명 89.5.
   무승부가 없도록 .5로 끊는다.

   기준선 하나가 곧 마켓 하나다. 여러 개를 열면 언더·오버를 여러 번 걸 수
   있어서 '어디에 건 건지' 자체가 헷갈리므로 경기마다 하나만 연다. */
export { KILLS_PER_PLAYER };

/* 게임과 모드마다 한 판에 나오는 킬이 다르다.
   롤 내전은 인당 9킬 언저리인데, 발로란트 일반은 라운드제라 절반도 안 되고
   신속은 그보다 더 적다. 난투는 킬만 주고받아서 제일 많다.
   (games.js의 modes[].killsPerPlayer) */
export const killLineFor = (playerCount, game, mode) => {
  const n = Number(playerCount) || 0;
  const per = getMode(game, mode).killsPerPlayer || getGame(game).killsPerPlayer;
  if (n === 0) return DEFAULT_KILL_LINE;
  return Math.round((per || KILLS_PER_PLAYER) * n) + 0.5;
};

/* 이 경기의 기준선. team_a/team_b는 배팅을 열 때 박혀서 그 뒤에 명단이
   바뀌어도 흔들리지 않는다. 그래서 언제 계산해도 같은 마켓 이름이 나온다 */
/* 이 경기의 기준선.

   방장이 또또를 열 때 직접 정했으면 그 값(scrims.kill_line)을 쓴다.
   안 정했으면 인원으로 계산한다. team_a/team_b는 열 때 박혀서 그 뒤에
   명단이 바뀌어도 흔들리지 않으니, 언제 계산해도 같은 마켓 이름이 나온다 */
export const killLineOfScrim = (scrim, game) =>
  scrim?.kill_line != null
    ? Number(scrim.kill_line)
    : killLineFor(
        (scrim?.team_a?.length || 0) + (scrim?.team_b?.length || 0),
        game,
        scrim?.mode
      );
export const killMarket = (line) => `kills_${line}`;
export const killLineOf = (market) => Number(market.split('_')[1]);

export const isKillMarket = (market) => market.startsWith('kills_');

/* 정산이 끝난 경기에서 이 마켓의 정답이 무엇이었는지.
   화면 세 곳(선택지 색, 내 배팅, 참여자 목록)이 같은 기준을 봐야 해서
   한 군데서만 판단한다. 결과를 안 넣은 마켓은 null(=전액 환불) */
export const winningSelection = (scrim, market) => {
  if (!scrim || scrim.status !== 'settled') return null;
  /* 일반 게임 또또는 마켓이 다르다. 한 군데서만 판단한다 (rules/casual) */
  if (scrim.kind === 'casual') return casualAnswer(scrim, market);
  if (market === 'winner') return scrim.winner ?? null;
  if (market === 'first_blood')
    return scrim.first_blood_player_id == null ? null : String(scrim.first_blood_player_id);
  if (isKillMarket(market)) {
    if (scrim.total_kills == null) return null;
    return scrim.total_kills > killLineOf(market) ? 'over' : 'under';
  }
  return null;
};

/* 고정 배당 마켓의 1인 상한. sql/setup.sql의 place_bets와 같아야 한다.
   숫자는 tuning.js에 있다 */
export { BET_CAP };

/* 상한은 tuning.js의 BET_CAP 한 곳에서만 정한다. null이면 상한 없음 */
export const capOf = (market) => {
  if (/^(ourkills|oppkills)_/.test(market)) return BET_CAP.team_kills ?? null;
  return (isKillMarket(market) ? BET_CAP.kills : BET_CAP[market]) ?? null;
};

export const marketLabel = (market) => {
  if (market === 'winner') return '승리팀';
  if (market === 'first_blood') return '첫 킬';
  if (market === 'kills_parity') return '킬 짝/홀';
  if (market === 'fb_side') return '첫 킬 - 어느 팀';
  if (market === 'dragon') return '첫 용';
  if (market === 'our_win') return '우리 팀 승리';
  if (market === 'parlay') return '묶음';
  if (market.startsWith('ourkills_')) return `우리 팀 킬 ${killLineOf(market)}`;
  if (market.startsWith('oppkills_')) return `상대 팀 킬 ${killLineOf(market)}`;
  if (isKillMarket(market)) return `총 킬 ${killLineOf(market)}`;
  return market;
};

export const agreeFairplay = () => rpc('agree_fairplay');

/* killLine을 빠뜨리면 안 된다. 서버가 null을 받으면 인원으로 다시
   계산해서 넣어버리는데, 화면은 '방장이 정한 값'을 보여주는 줄 알고
   있으니 아무 오류 없이 조용히 다른 기준선으로 정산된다.
   (실제로 openBettingByNames는 넘기는데 여기서 안 받고 있었다) */
export const openBetting = (roomId, mode, teamA, teamB, closeSeconds = null, killLine = null) =>
  rpc('open_betting', {
    p_room: roomId,
    p_mode: mode,
    p_team_a: teamA,
    p_team_b: teamB,
    p_close_seconds: closeSeconds,
    p_kill_line: killLine,
  });

/* 퍼블 배당. 고정 배당이라 누가 얼마를 걸었는지와 무관해서 마감 전에
   보여줘도 눈치싸움이 안 생긴다 - 오히려 배당을 모르고 걸게 두는 쪽이
   이상했다.

   정산 때 쓰는 값과 같은 함수에서 나온다. 화면에서 따로 계산하면
   '걸 때 본 배당'과 '받은 배당'이 조용히 달라진다.

   경기가 열려 있는 동안에는 값이 안 변하므로 (지난 판과 명단으로만
   정해진다) 폴링마다 부르지 않는다 - 경기가 바뀔 때만 한 번 */
export const fetchFbOdds = async (scrimId) => {
  if (!isNeonConfigured || !scrimId) return new Map();
  const rows = await rpc('fb_odds', { p_scrim: scrimId }).catch(() => []);
  return new Map((rows || []).map((r) => [Number(r.player_id), Number(r.odds)]));
};

export const placeBets = (scrimId, bets) =>
  rpc('place_bets', { p_scrim: scrimId, p_bets: bets });

export const lockBetting = (scrimId) => rpc('lock_betting', { p_scrim: scrimId });

/* 일반 게임 또또 열기. 내전과 달리 팀이 하나뿐이고(우리 다섯) 라인을
   같이 넘긴다 - 첫 킬 배당이 라인마다 다르다 */
export const openCasualBet = ({ roomId, mode, playerIds, lanes, closeSeconds, killLine }) =>
  rpc('open_casual_bet', {
    p_room: roomId,
    p_mode: mode,
    p_players: playerIds,
    p_lanes: lanes || {},
    p_close_seconds: closeSeconds ?? null,
    p_kill_line: killLine,
  });

/* 일반 게임 또또 정산. 내전과 함수가 다르다 - 내전은 '이긴 팀'이 반드시
   있어야 하고 마켓도 서로 다르다 */
export const settleCasual = (
  scrimId,
  { ourKills, oppKills, firstBloodPlayerId, fbSide, fbLane, fbAlly, dragon, ourWin }
) =>
  rpc('settle_casual', {
    p_scrim: scrimId,
    p_our_kills: ourKills ?? null,
    p_opp_kills: oppKills ?? null,
    p_first_blood: firstBloodPlayerId ?? null,
    p_fb_side: fbSide ?? null,
    p_fb_lane: fbLane ?? null,
    p_dragon: dragon ?? null,
    /* 우리 명단에 없는 우리 팀원이 땄을 때: 그 라인, 또는 'ANY'(모름) */
    p_fb_ally: fbAlly ?? null,
    /* true 이김 · false 짐 · null 안 넣음 (우리 팀 승리 환불) */
    p_our_win: ourWin ?? null,
  });

/* 배팅 묶기. 다리마다 { market, selection }만 보낸다 - 배당은 서버가 다시
   매긴다. 화면이 보낸 배당을 믿으면 콘솔에서 100배로 바꿔 보낼 수 있다.
   돌려받는 값은 서버가 박은 묶음 배당이다 */
export const placeParlay = (scrimId, legs, amount) =>
  rpc('place_parlay', {
    p_scrim: scrimId,
    p_legs: legs.map(({ market, selection }) => ({ market, selection })),
    p_amount: amount,
  });

export const settleScrim = (scrimId, winner, totalKills, firstBloodPlayerId) =>
  rpc('settle_scrim', {
    p_scrim: scrimId,
    p_winner: winner,
    p_total_kills: totalKills,
    p_first_blood: firstBloodPlayerId,
  });

export const unsettleScrim = (scrimId) => rpc('unsettle_scrim', { p_scrim: scrimId });

/* 배당과 배팅은 방을 열 때마다 통째로 받으면 안 된다. 경기가 1000개까지
   쌓이므로 지금 화면에 띄울 몇 경기 것만 골라 받는다 */
export const fetchBetting = async (scrimIds, liveId = null) => {
  if (!isNeonConfigured || scrimIds.length === 0) return { pools: [], bets: [], bettors: [] };
  const list = `(${scrimIds.join(',')})`;
  const pools = unwrap(await neon.from('bet_pools').select('*').filter('scrim_id', 'in', list));
  const bets = unwrap(await neon.from('bets').select('*').filter('scrim_id', 'in', list));
  /* 마감 전에는 남의 배팅 줄 자체가 안 보인다. 이름만 따로 받아온다.
     끝난 판은 bets에 다 들어 있으니 진행 중인 판에만 한 번 더 묻는다 */
  const bettors = liveId
    ? await rpc('scrim_bettors', { p_scrim: liveId }).catch(() => [])
    : [];
  return { pools: pools || [], bets: bets || [], bettors: bettors || [] };
};

/* 같은 사람이 이름을 바꿔 가며 두 줄로 쌓인 것을 합친다.
   지난 경기가 들고 있는 id까지 갈아끼워야 전적이 붙는다 - 서버에서 한다.
   합쳐진 경기 수를 돌려준다 */
export const mergeRoomPlayers = (keepId, dropId) =>
  rpc('merge_room_players', { p_keep: keepId, p_drop: dropId });

/* 사람별 퍼스트 블러드 비율.
   퍼블이 적힌 판만 센다 - 안 적은 판을 분모에 넣으면 전부 실제보다 낮게 나온다.

   1판 1퍼블을 100%라고 내보내면 그 사람에게 돈이 몰린다. 순위표에서 쓰는
   것과 같은 베이지안 스무딩으로, 판수가 적으면 '아무나 딸 확률'(10인이면
   10%) 쪽으로 끌어당긴다. 판이 쌓이면 실제 비율로 수렴한다 */
/* 내전 첫 킬 배당. sql/setup.sql의 fb_odds와 같은 식이다.
     인원 × 0.85 × 티어(골드 기준 한 칸당 2%) × 첫 킬 비율(±5%)
   화면은 서버 값(fb_odds)을 먼저 쓴다. 이건 서버 값이 아직 안 왔거나 못
   받았을 때 배당 칸이 비지 않게 하는 것 - 고정 배당이라 걸 때 보여야 한다.
   stat은 firstBloodRates의 한 줄 ({ games, rate }) */
export const scrimFbOdds = ({ size, tierIdx, stat }) => {
  const idx = tierIdx == null || tierIdx < 0 ? 3 : tierIdx;
  const tier = 1 + (3 - idx) * FIRST_BLOOD_TIER_BONUS;
  const tilt = !stat?.games
    ? 1
    : Math.max(
        FIRST_BLOOD_TILT_CLAMP.min,
        Math.min(FIRST_BLOOD_TILT_CLAMP.max, 1 - FIRST_BLOOD_RATE_TILT * (stat.rate * size - 1))
      );
  return Math.round(size * FIRST_BLOOD_RATE * tier * tilt * 100) / 100;
};

export const firstBloodRates = (scrims = []) => {
  const by = new Map();
  scrims.forEach((s) => {
    /* 일반 큐 판은 우리 다섯만 있어 '다섯 중 하나'로 세어진다. 섞으면
       내전(열 명 중 하나) 확률이 부풀려진다 */
    if (s.status !== 'settled' || !s.first_blood_player_id || s.kind === 'casual') return;
    const roster = [...(s.team_a || []), ...(s.team_b || [])];
    if (roster.length === 0) return;
    roster.forEach((id) => {
      const cur = by.get(Number(id)) || { games: 0, got: 0, expected: 0 };
      cur.games += 1;
      cur.expected += 1 / roster.length;
      if (Number(s.first_blood_player_id) === Number(id)) cur.got += 1;
      by.set(Number(id), cur);
    });
  });
  by.forEach((v) => {
    const base = v.expected / v.games;
    v.rate = (v.got + PRIOR_GAMES * base) / (v.games + PRIOR_GAMES);
  });
  return by;
};
